import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Chart from "react-apexcharts";

function CompanyChart({ tickerSelected, currentUser }) {
  const [timeRange, setTimeRange] = useState("7D");
  const [chartData, setChartData] = useState({ "7D": [], "30D": [], "90D": [] });
  const [ohlcData, setOhlcData] = useState({ "7D": [], "30D": [], "90D": [] });
  const [loading, setLoading] = useState(false);
  const [showCandles, setShowCandles] = useState(false);
  const [debouncedTicker, setDebouncedTicker] = useState(tickerSelected);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const reqSeqRef = useRef(0);

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!tickerSelected) {
      setDebouncedTicker(undefined);
      return;
    }
    const t = setTimeout(() => setDebouncedTicker(tickerSelected), 250);
    return () => clearTimeout(t);
  }, [tickerSelected]);

  useEffect(() => {
    if (!debouncedTicker) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const mySeq = ++reqSeqRef.current;
    setLoading(true);

    (async () => {
      try {
        const token = await currentUser.getIdToken(false);
        const res = await fetch("https://getcompanycharts-5dhfwyjvzq-uc.a.run.app", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ tickerSelected: debouncedTicker }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mySeq !== reqSeqRef.current || !mountedRef.current) return;

        setChartData({
          "7D": data.companyChart7D || [],
          "30D": data.companyChart30D || [],
          "90D": data.companyChart90D || [],
        });
        setOhlcData({
          "7D": data.ohlcData7D || [],
          "30D": data.ohlcData30D || [],
          "90D": data.ohlcData90D || [],
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("Chart fetch error:", err);
      } finally {
        if (mountedRef.current && mySeq === reqSeqRef.current) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [debouncedTicker, currentUser]);

  const addIndicators = useCallback((data, period = 20) => {
    let emaPrev = null;
    const k = 2 / (period + 1);
    return (data || []).map((point, idx, arr) => {
      const start = Math.max(0, idx - period + 1);
      const slice = arr.slice(start, idx + 1).map((p) => Number(p.Price || 0));
      
      const high = slice.length ? Math.max(...slice) : 0;
      const low = slice.length ? Math.min(...slice) : 0;
      const mid = (high + low) / 2;

      const sma = slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : 0;
      const priceNow = Number(point.Price || 0);
      const ema = emaPrev === null ? priceNow : priceNow * k + emaPrev * (1 - k);
      emaPrev = ema;
      const variance = slice.length ? slice.reduce((sum, val) => sum + Math.pow(val - sma, 2), 0) / slice.length : 0;
      const stdDev = Math.sqrt(variance);
      
      return {
        ...point,
        SMA: sma,
        EMA: ema,
        DonchianHigh: high,
        DonchianLow: low,
        DonchianMid: mid,
        BollingerUpper: sma + 2 * stdDev,
        BollingerLower: sma - 2 * stdDev,
      };
    });
  }, []);

  const displayedData = useMemo(() => {
    const raw = chartData[timeRange] || [];
    return addIndicators(raw, timeRange === "7D" ? 3 : timeRange === "30D" ? 10 : 20);
  }, [chartData, timeRange, addIndicators]);

  const isBondTicker = ["BND3", "BND7", "BND14"].includes(debouncedTicker);
  const priceLabel = isBondTicker ? "Interest Rate" : "Price";
  const effectiveShowCandles = isBondTicker ? false : showCandles;

  const totalChange = useMemo(() => {
    if (loading || (displayedData || []).length < 2) return null;
    const first = Number(displayedData[0].Price || 0);
    const last = Number(displayedData[displayedData.length - 1].Price || 0);
    const diff = last - first;
    return { diff, percent: first !== 0 ? (diff / first) * 100 : 0 };
  }, [displayedData, loading]);

  const mapped = useMemo(() => {
    return displayedData.map((d) => ({
      x: d.x || d.Date,
      Price: round2(d.Price),
      SMA: round2(d.SMA),
      EMA: round2(d.EMA),
      DonchianHigh: round2(d.DonchianHigh),
      DonchianLow: round2(d.DonchianLow),
      DonchianMid: round2(d.DonchianMid),
      BollingerUpper: round2(d.BollingerUpper),
      BollingerLower: round2(d.BollingerLower),
    }));
  }, [displayedData]);

  const series = useMemo(() => {
    if (loading || !mapped.length) return [];
    if (effectiveShowCandles) {
      return [{
        name: "Candles",
        type: "candlestick",
        data: (ohlcData[timeRange] || []).map((d) => ({
          x: d.x || d.Date,
          y: [round2(d.open), round2(d.high), round2(d.low), round2(d.close)],
        })),
      }];
    }
    return [
      { name: priceLabel, type: "line", data: mapped.map((d) => ({ x: d.x, y: d.Price })) },
      { name: "Donchian High", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.DonchianHigh })) },
      { name: "Donchian Low", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.DonchianLow })) },
      { name: "Donchian Mid", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.DonchianMid })) },
      { name: "SMA", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.SMA })) },
      { name: "EMA", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.EMA })) },
      { name: "Bollinger Upper", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.BollingerUpper })) },
      { name: "Bollinger Lower", type: "line", data: mapped.map((d) => ({ x: d.x, y: d.BollingerLower })) },
    ];
  }, [loading, mapped, effectiveShowCandles, ohlcData, timeRange, priceLabel]);

const options = useMemo(() => ({
  chart: { 
    type: effectiveShowCandles ? "candlestick" : "line", 
    background: "transparent", 
    toolbar: { show: false }, 
    animations: { enabled: false }
  },
  colors: ["#50e3c2", "#ff9800", "#03a9f4", "#9c27b0", "#4caf50", "#f44336", "#e91e63", "#2196f3"],
  legend: { 
    show: true, 
    position: 'top',
    horizontalAlign: 'center',
    fontSize: '10px',
    fontFamily: 'JetBrains Mono, monospace',
    labels: { colors: "#fff" },
    markers: { 
      width: 8, 
      height: 8,
      strokeWidth: 0,
      fillOpacity: 1
    },
    itemMargin: { horizontal: 5, vertical: 2 },
    onItemClick: { toggleDataSeries: true }
  },
  xaxis: { 
    type: "category", 
    labels: { 
      style: { 
        colors: "#fff", 
        fontSize: '9px',
        cssClass: 'apexcharts-xaxis-label',
      },
      rotate: -45,
      maxHeight: 30
    },
    axisBorder: { show: false },
    tooltip: { enabled: false }
  },
  yaxis: { 
    labels: { 
      formatter: (val) => Number(val || 0).toFixed(1), 
      style: { colors: "#fff", fontSize: '9px' },
      offsetX: -10 
    } 
  },
  tooltip: { 
    theme: "dark", 
    shared: true,
    intersect: false,
    y: {
      formatter: (val, { seriesIndex, dataPointIndex, w }) => {
        const series = w.config.series[seriesIndex];
        if (!series) return "";

        const isCandle = series.type === 'candlestick';
        
        if (effectiveShowCandles && isCandle) {
          try {
            const o = w.globals.seriesCandleO[seriesIndex][dataPointIndex];
            const h = w.globals.seriesCandleH[seriesIndex][dataPointIndex];
            const l = w.globals.seriesCandleL[seriesIndex][dataPointIndex];
            const c = w.globals.seriesCandleC[seriesIndex][dataPointIndex];
            
            if (o === undefined || isNaN(o)) return "LOADING...";
            
            return `O: ${o.toFixed(2)} H: ${h.toFixed(2)} L: ${l.toFixed(2)} C: ${c.toFixed(2)}`;
          } catch (e) {
            return "---";
          }
        }
        return val !== undefined && val !== null ? val.toFixed(2) : "";
      }
    }
  },
  grid: { 
    borderColor: "#333",
    padding: { left: 10, right: 10, top: 0, bottom: 0 } 
  },
  stroke: { 
    width: effectiveShowCandles ? 1 : 2, 
    curve: 'smooth' 
  },
  states: {
    active: { filter: { type: 'none' } },
    hover: { filter: { type: 'none' } },
    inactive: { opacity: 0.45 }
  }
}), [effectiveShowCandles]);
  return (
    <div style={{ width: "100%", padding: "4px", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "4px" }}>
        <h2 style={{ color: "#50e3c2", margin: 0, fontSize: "0.85rem", textTransform: "uppercase" }}>
          {debouncedTicker ? `>$${debouncedTicker}` : "LOADING..."}
        </h2>
        {totalChange && (
          <div style={{ color: totalChange.diff >= 0 ? "#4caf50" : "#f44336", fontSize: "0.65rem" }}>
            {totalChange.diff >= 0 ? "+" : ""}{round2(totalChange.diff)} ({round2(totalChange.percent)}%)
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: "4px", marginBottom: "10px", width: "100%" }}>
        {["7D", "30D", "90D"].map((range) => (
          <button
            key={range}
            className={`jetbrains-btn ${timeRange === range ? "" : "disabled"}`}
            style={{
              flex: 1,
              background: timeRange === range ? "rgba(80, 227, 194, 0.2)" : "rgba(0,0,0,0.2)",
              color: timeRange === range ? "#50e3c2" : "#888",
              border: `1px solid ${timeRange === range ? "#50e3c2" : "#444"}`,
              padding: "6px 0",
              fontSize: "0.6rem",
              cursor: "pointer",
              borderRadius: "2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "24px"
            }}
            onClick={() => setTimeRange(range)}
          >
            {range}
          </button>
        ))}
        {!isBondTicker && (
          <button
            className="jetbrains-btn"
            style={{
              flex: 1.5,
              background: "rgba(0,0,0,0.2)",
              color: "#50e3c2",
              border: "1px solid #50e3c2",
              padding: "6px 0", 
              fontSize: "0.6rem",
              cursor: "pointer",
              borderRadius: "2px",
              minHeight: "24px"
            }}
            onClick={() => setShowCandles((prev) => !prev)}
          >
            {showCandles ? "LINE" : "CANDLE"}
          </button>
        )}
      </div>

      {loading || series.length === 0 ? (
        <div style={{ color: "#50e3c2", textAlign: "center", padding: "40px", fontSize: "0.6rem" }}>
          LOADING...
        </div>
      ) : (
        <div style={{ marginLeft: "-15px" }}>
          <Chart 
            key={`${debouncedTicker}-${timeRange}-${effectiveShowCandles ? 'candle' : 'line'}`}
            options={options} 
            series={series} 
            type={effectiveShowCandles ? "candlestick" : "line"} 
            height={400} 
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(CompanyChart);